/**
 * CORE-888 — куда уходит мастер после успешного создания эфира.
 *
 * Визард создания уводил на следующий экран через `navigate`, то есть КЛАЛ его
 * сверху, оставаясь смонтированным под низом. А `submitLock`/`submitting` там
 * снимаются только на ошибке — на успехе они намеренно остаются взведёнными,
 * чтобы залипшее нажатие не создало вторую сессию. Пара из этих двух решений и
 * давала тупик из тикета: любой «Назад» находил под низом визард уже созданного
 * эфира, с кнопками в вечном loader'е и без способа его отменить. На вебе то же
 * самое отдельно жило в history — браузерный Back возвращал в полузавершённый
 * `/live-session/create`.
 *
 * Лечится не хендлерами «Назад» на экранах эфира — они и так все идут через
 * `goBackFromLive` (см. [[liveBackNav]]) и делают ровно то, что должны: снимают
 * верхний экран. Лечится стеком: у ЗАПЛАНИРОВАННОГО эфира визард после создания
 * завершён, поэтому он заменяется целиком — под целевым экраном остаётся Лента.
 * Тогда `canGoBack()` по-прежнему true, `goBack()` ведёт в Ленту и из зелёной
 * комнаты, и из комнаты ожидания, а `reset` переписывает и web-history.
 *
 * Мгновенный эфир (быстрая комната) намеренно остаётся на `navigate`: это своя
 * ветка со своей историей (CORE-583), а тикет — про запланированный сценарий.
 * Сама сессия при выходе не трогается: она уже создана на сервере.
 *
 * Чистая функция, без импортов RN, — чтобы жить в node-окружении jest, как
 * `liveBackNav.ts` рядом.
 */

/** Экран, на который визард уводит после создания. */
export interface PostCreateRoute {
  name: string;
  params?: object;
}

export type PostCreateNav =
  /** Прежнее поведение: положить экран сверху, визард остаётся в стеке. */
  | { type: 'navigate'; name: string; params?: object }
  /** Заменить стек целиком: [Лента, целевой экран]. */
  | { type: 'reset'; index: number; routes: PostCreateRoute[] };

/** Лента — вкладка внутри дашборда; тот же адрес, что у пункта меню «Лента». */
export const FEED_ROUTE: PostCreateRoute = { name: 'Dashboard', params: { initialTab: 'feed' } };

/**
 * CORE-1168 — на какой экран визард уводит после создания.
 *
 * Зелёная комната — это проверка камеры и микрофона перед входом в идущий
 * эфир. Мастеру, который эфир ЗАПЛАНИРОВАЛ, проверять нечего: сессия будет
 * позже, а устройства он проверит перед ней. Он видел «как будто перед началом
 * уже лайва» и уходил оттуда браузерным «Назад», не понимая, опубликовалась ли
 * сессия. Нативный визард так себя не ведёт с самого начала
 * (`LiveWizard.native.tsx`: «Запланировать» не значит «войти») — расходился
 * только веб.
 *
 * Правило одно: зелёная комната только у эфира, который начинается СЕЙЧАС.
 * Всё запланированное — одиночное так же, как серия — уходит на анонс уже
 * созданной сессии.
 */
/**
 * CORE-1168 — пережило ли расписание сервер.
 *
 * Сервер вправе молча снять дату: у создателя без capability
 * `session.schedule` поле `ScheduledAtUtc` выбрасывается (CORE-544,
 * silent-downgrade), и сессия создаётся мгновенной и приватной. Он честно
 * перечисляет снятое в `downgradedFields`, но экран создания туда не смотрел —
 * и уводил мастера на анонс «опубликовано», пока комната уже шла. Анонс,
 * увидев Active-комнату, сам через пару секунд перебрасывал его в зелёную
 * комнату: ровно тот экран, ради ухода от которого этот тикет и заведён.
 * Поймано QA на staging 02.09.2026 (комната 1000261: `ScheduledAtUtc = null`,
 * `downgradedFields = [ScheduledAtUtc, Format]`).
 *
 * Поэтому решение принимается по тому, что сервер СОЗДАЛ, а не по тому, что мы
 * просили. Отсутствие поля значит «сервер ничего не снимал» — старый бэкенд без
 * `downgradedFields` ведёт себя как раньше.
 */
export const scheduleSurvivedCreate = (downgradedFields?: string[] | null): boolean =>
  !(downgradedFields ?? []).includes('ScheduledAtUtc');

export const resolvePostCreateTarget = (opts: {
  /** `launchMode === 'scheduled'` — эфир запланирован, а не начинается сейчас. */
  scheduled: boolean;
  /** Серия по правилу повторения. В запросе создания поле необязательное,
   *  и «не передали» здесь значит «не серия», а не «неизвестно». */
  isRecurring: boolean | undefined;
  /** Анонс созданной сессии. */
  announcement: PostCreateRoute;
  /** Зелёная комната — проверка устройств перед входом. */
  greenRoom: PostCreateRoute;
}): PostCreateRoute => (
  opts.scheduled || !!opts.isRecurring ? opts.announcement : opts.greenRoom
);

export const resolvePostCreateNav = (opts: {
  /** `launchMode === 'scheduled'` — эфир запланирован, а не начинается сейчас. */
  scheduled: boolean;
  /** Куда экран уводил и уводит: зелёная комната или анонс серии. */
  target: PostCreateRoute;
}): PostCreateNav => {
  if (!opts.scheduled) {
    return { type: 'navigate', name: opts.target.name, params: opts.target.params };
  }
  return { type: 'reset', index: 1, routes: [FEED_ROUTE, opts.target] };
};

/** Тот кусок навигатора, который здесь нужен. `navigate`/`reset` намеренно
 *  свободны по типам: настоящие перегружены по маршрутам. */
export interface PostCreateNavigator {
  navigate: (...args: any[]) => void;
  reset: (state: { index: number; routes: PostCreateRoute[] }) => void;
}

/** Применить решение выше к навигатору. */
export const applyPostCreateNav = (nav: PostCreateNavigator, decision: PostCreateNav): void => {
  if (decision.type === 'navigate') nav.navigate(decision.name, decision.params);
  else nav.reset({ index: decision.index, routes: decision.routes });
};
